import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Pencil,
  PencilIcon,
  Eye,
  Calendar as CalendarIcon,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { formattedDate } from "../../../../lib/utils";
import { ControlWithUser } from "../../../../models/audit/control";
import { TestPhase } from "../../../../models/audit/controlSession";
import { CreateSessionDialog } from "./CreateSessionDialog";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { fetchFastApi } from "../../../../api/fastapi";
import { toast } from "sonner";
import { DeleteSessionDialog } from "../../../../components/audit/DeleteSessionDialog";
import { SystemOfRecordBadge } from "../../../../components/SystemOfRecordBadge";
import { useAuth } from "../../../../contexts/authContext";
import { getExternalControlUrl } from "../../../../lib/systemOfRecord";

interface ControlSession {
  id: string;
  name: string;
  status?: string | null;
  createdAt: string;
  user: {
    email: string;
  };
}

interface ControlDetailPanelProps {
  control: ControlWithUser;
  sessions: ControlSession[];
  loadingSessions: boolean;
  onClose: () => void;
  isCreateSessionDialogOpen: boolean;
  onCreateSessionDialogOpenChange: (open: boolean) => void;
  newSessionName: string;
  testPhase: TestPhase;
  onTestPhaseChange: (testPhase: TestPhase) => void;
  fyCycle: string;
  onFyCycleChange: (fyCycle: string) => void;
  fyCycleOptions: string[];
  onNewSessionNameChange: (name: string) => void;
  onCreateSession: (optroTestId: number | null) => void;
  loadingCreateSession: boolean;
  canEditControl: boolean;
  canEditTest: boolean;
  onSessionDeleted: () => void;
}

export const ControlDetailPanel = ({
  control,
  sessions,
  loadingSessions,
  onClose,
  isCreateSessionDialogOpen,
  onCreateSessionDialogOpenChange,
  newSessionName,
  onNewSessionNameChange,
  testPhase,
  onTestPhaseChange,
  fyCycle,
  onFyCycleChange,
  fyCycleOptions,
  onCreateSession,
  loadingCreateSession,
  canEditControl,
  canEditTest,
  onSessionDeleted,
}: ControlDetailPanelProps) => {
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const externalControlUrl =
    workspace?.systemOfRecord && control.externalRef
      ? getExternalControlUrl(
          workspace.systemOfRecord,
          control.externalRef.externalRef,
        )
      : null;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ControlSession | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent, session: ControlSession) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/controls/v2/control-sessions/${sessionToDelete.id}`,
      });

      toast.success("Test deleted successfully");
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
      // Trigger refetch immediately after successful delete
      onSessionDeleted();
    } catch (error) {
      console.error("Failed to delete session:", error);
      toast.error("Failed to delete test");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="h-screen flex flex-col border-l bg-white overflow-y-auto"
      data-testid="control-detail-panel"
    >
      {/* Header */}
      <div className="flex justify-between p-6 border-b">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-medium text-gray-900 truncate">
              {control.name}
            </h2>
            <span
              data-testid="control-detail-panel-state-badge"
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                control.configuredState === "configured"
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {control.configuredState === "configured"
                ? "Configured"
                : control.configuredState === "not_configured"
                  ? "Not Configured"
                  : "Draft"}
            </span>
            {control.externalRef && (
              <SystemOfRecordBadge
                externalRef={control.externalRef}
                controlUrl={externalControlUrl}
              />
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            <span>Created {formattedDate(control.createdAt)}</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 p-6 space-y-6">
        {/* Open Control Configuration Button */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="gap-2 w-full"
              onClick={() => navigate(`/audit/controls/v2/${control.id}`)}
            >
              {canEditControl ? (
                <Pencil className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {canEditControl ? "Edit Control" : "View Control"}
            </Button>
          </div>
        </div>
        {/* Description Section */}
        {control.description && (
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Description
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed max-h-[200px] overflow-y-auto">
              <Markdown remarkPlugins={[remarkGfm]}>
                {control.description}
              </Markdown>
            </p>
          </div>
        )}

        {/* Details Section */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Details
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div
              className="space-y-1"
              data-testid="control-detail-panel-owner-field"
            >
              <p className="text-xs text-gray-500">Control Owner</p>
              <p className="text-sm text-gray-900">
                {control.controlOwner || "—"}
              </p>
            </div>
            <div
              className="space-y-1"
              data-testid="control-detail-panel-cycle-field"
            >
              <p className="text-xs text-gray-500">Cycle</p>
              <p className="text-sm text-gray-900">{control.cycle || "—"}</p>
            </div>
            <div
              className="space-y-1"
              data-testid="control-detail-panel-process-field"
            >
              <p className="text-xs text-gray-500">Process</p>
              <p className="text-sm text-gray-900">{control.process || "—"}</p>
            </div>
            {control.externalRef && (
              <div className="space-y-1">
                <p className="text-xs text-gray-500">System of Record</p>
                <div className="text-sm">
                  <SystemOfRecordBadge
                    externalRef={control.externalRef}
                    controlUrl={externalControlUrl}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tests Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900">Tests</h3>
            <div className="flex items-center gap-2">
              <CreateSessionDialog
                controlId={control.id}
                open={isCreateSessionDialogOpen}
                onOpenChange={onCreateSessionDialogOpenChange}
                sessionName={newSessionName}
                testPhase={testPhase}
                onTestPhaseChange={onTestPhaseChange}
                fyCycle={fyCycle}
                onFyCycleChange={onFyCycleChange}
                fyCycleOptions={fyCycleOptions}
                onSessionNameChange={onNewSessionNameChange}
                onCreateSession={onCreateSession}
                loading={loadingCreateSession}
                disabled={!canEditTest}
              />
            </div>
          </div>

          {loadingSessions ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : sessions.length > 0 ? (
            <div className="space-y-3">
              {sessions.map((session) => (
                <a
                  key={session.id}
                  href={`/audit/control-sessions/session/${session.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block border p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 text-sm">
                          {session.name}
                        </h4>
                        <Badge
                          variant={
                            session.status === "complete"
                              ? "default"
                              : session.status === "in_review"
                                ? "outline"
                                : "secondary"
                          }
                          className="flex-shrink-0"
                        >
                          {session.status === "complete"
                            ? "Complete"
                            : session.status === "in_review"
                              ? "In Review"
                              : "In Progress"}
                        </Badge>
                      </div>
                    </div>
                    {canEditTest && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={(e) => handleDeleteClick(e, session)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Test
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          <span>
                            Created at {formattedDate(session.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <PencilIcon className="h-3 w-3" />
                      <span className="text-xs text-gray-500">
                        Created by {session.user.email}
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 space-y-3">
              <div className="text-gray-400">
                <CalendarIcon className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">No tests found</p>
                <p className="text-xs text-gray-500">
                  Tests will appear here when created
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteSessionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        isDeleting={isDeleting}
        sessionName={sessionToDelete?.name}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
